/**
 * MasterComponentsCategoriesTab - Iter 9.40+ Slice C²
 *
 * Per-category branding panel matching how each category is rendered platform-wide:
 *   • Square category icon (the icon IS the host's default brand-mark / logo)
 *   • Light + Dark hex codes (matches host dashboard branding settings)
 *   • Separate full Logo image upload (used in envelope, host nav, RSVP cover)
 *   • Envelope cover, Welcome image, Confirmation image (UPLOAD ONLY - no URL fields)
 *
 * "Linked from" pulls real names - every host using this category, every product /
 * brand / service / occasion / experience / feature that consumes it. Click a row
 * to see the full list.
 */
import { useState, useEffect, useCallback, useRef } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Loader2, Upload, Image as ImgIcon, Layers, ChevronDown, ChevronRight } from "lucide-react";
import { setLiveCategoryOverride, refreshLiveCategoryMeta } from "@/lib/categoryConfig";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem("session_token")}` });

// Iter 9.40 Slice C⁸ - only icon + envelope cover are kept. The full logo
// inherits from the category icon by default (host can override per-host),
// and welcome / confirmation images were unused so we dropped them.
const IMAGE_FIELDS = [
  {
    key: "envelope_cover_url",
    label: "Envelope cover",
    used_in: ["Host invite envelope", "Plan-a-Service preview hero", "Public category landing"],
  },
];

const ICON_USED_IN = [
  "Admin Product sidebar", "Host Dashboard top header",
  "Plan-a-Service tile", "Plan-an-Occasion tile",
  "Kanban inspo placeholder background", "Preference card avatar fallback",
  "Launch Control Categories card", "Launch Control Cities matrix top row",
];

const COLLECTION_LABELS = {
  host_dashboards:    "Host dashboards",
  master_products:    "Products",
  master_brands:      "Brands",
  master_services:    "Services",
  master_occasions:   "Occasions",
  master_experiences: "Experiences",
  master_features:    "Features",
};

export default function MasterComponentsCategoriesTab() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/admin/categories`, { headers: auth() });
      if (r.ok) {
        const d = await r.json();
        const cats = d.categories || [];
        const enriched = await Promise.all(cats.map(async (c) => {
          try {
            const r2 = await fetch(`${API}/admin/categories/${c.category_id}/linked-from`, { headers: auth() });
            if (r2.ok) {
              const lf = await r2.json();
              return { ...c, linked_from: lf.linked_from, linked_details: lf.details };
            }
          } catch { /* ignore */ }
          return c;
        }));
        setCategories(enriched);
      }
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const updateBranding = async (catId, patch) => {
    const r = await fetch(`${API}/admin/categories/${catId}/branding`, {
      method: "PUT", headers: { ...auth(), "Content-Type": "application/json" },
      body: JSON.stringify(patch),
    });
    if (r.ok) {
      setCategories(cs => cs.map(c => c.category_id === catId ? { ...c, ...patch } : c));
      // Iter 9.40 Slice C⁸ - push the override immediately so every consumer
      // (sidebar, Kanban editor, Launch Control) reflects the change without a
      // page refresh, then re-fetch live-meta so other tabs/users see it too.
      const shortKey = catId.replace(/^cat_/, "").toLowerCase();
      setLiveCategoryOverride(shortKey, {
        light_hex: patch.color_light || patch.light_hex,
        dark_hex:  patch.color_dark  || patch.dark_hex,
        icon_url:  patch.icon_url,
        label:     patch.label,
      });
      try { await refreshLiveCategoryMeta(); } catch {/* non-blocking */}
      toast.success("Saved - synced platform-wide");
    }
    else toast.error("Save failed");
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>;

  return (
    <div className="space-y-3" data-testid="mc-categories-tab">
      <p className="text-xs text-stone-500 mb-2">
        Each category's brand: <strong>square icon</strong> (the default host brand-mark, also the host's default logo unless they override it) and <strong>light + dark hex codes</strong>. The envelope cover is a separate image. Edits sync platform-wide on save.
      </p>
      {categories.map(cat => (
        <CategoryBrandingCard key={cat.category_id} category={cat} onSave={updateBranding} />
      ))}
    </div>
  );
}

function CategoryBrandingCard({ category, onSave }) {
  const init = {
    color_light: category.light_hex || category.color_light || category.primary_color || "#F9FAFB",
    color_dark:  category.dark_hex  || category.color_dark  || category.accent_color  || "#111827",
    icon_url:               category.icon_url || "",
    envelope_cover_url:     category.envelope_cover_url || "",
  };
  const [draft, setDraft] = useState(init);
  const [linkedOpen, setLinkedOpen] = useState(false);

  useEffect(() => { setDraft(init); /* eslint-disable-next-line */ }, [category]);

  const dirty = Object.entries(draft).some(([k, v]) => (init[k] || "") !== v);
  const linkedFrom = category.linked_from || {};
  const details = category.linked_details || [];
  const linkedTotal = Object.values(linkedFrom).reduce((a, b) => a + (b || 0), 0);

  return (
    <Card data-testid={`mc-cat-card-${category.category_id}`}>
      <CardContent className="p-5">
        {/* Header with SQUARE icon (matches platform shape) */}
        <div className="flex items-center gap-3 mb-4">
          <div
            className="w-14 h-14 flex items-center justify-center overflow-hidden border border-stone-200"
            style={{ backgroundColor: draft.color_light, color: draft.color_dark, borderRadius: 10 }}
            data-testid={`mc-cat-icon-${category.category_id}`}
          >
            {draft.icon_url ? (
              <img src={draft.icon_url} alt="" className="w-full h-full object-cover" />
            ) : (
              <span className="text-xl font-bold">{(category.label || category.display_name || category.name || category.category_id || "?")[0].toUpperCase()}</span>
            )}
          </div>
          <div className="flex-1">
            <h3 className="text-base font-semibold capitalize">{category.label || category.display_name || category.name || category.category_id}</h3>
            <p className="text-[11px] text-stone-500 capitalize">{category.category_id}</p>
          </div>
          <Badge className="bg-stone-100 text-stone-700 border-0 flex items-center gap-1 text-[10px]">
            <Layers className="w-3 h-3" />Linked from {linkedTotal} surfaces
          </Badge>
        </div>

        {/* Light + Dark hex codes - matches host dashboard branding settings */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div className="flex items-center gap-2">
            <input type="color" value={draft.color_light} onChange={(e) => setDraft(d => ({ ...d, color_light: e.target.value }))} className="w-10 h-10 rounded cursor-pointer border border-stone-200" data-testid={`mc-cat-${category.category_id}-light`} />
            <div className="flex-1">
              <Label className="text-[10px] text-stone-500">Light hex</Label>
              <Input value={draft.color_light} onChange={(e) => setDraft(d => ({ ...d, color_light: e.target.value }))} className="h-7 font-mono text-[11px]" />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <input type="color" value={draft.color_dark} onChange={(e) => setDraft(d => ({ ...d, color_dark: e.target.value }))} className="w-10 h-10 rounded cursor-pointer border border-stone-200" data-testid={`mc-cat-${category.category_id}-dark`} />
            <div className="flex-1">
              <Label className="text-[10px] text-stone-500">Dark hex</Label>
              <Input value={draft.color_dark} onChange={(e) => setDraft(d => ({ ...d, color_dark: e.target.value }))} className="h-7 font-mono text-[11px]" />
            </div>
          </div>
        </div>

        {/* Square icon upload - IS the host's default brand-mark + logo */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
          <ImageUploader
            category={category}
            fieldKey="icon_url"
            label="Category icon"
            value={draft.icon_url}
            onChange={(url) => setDraft(d => ({ ...d, icon_url: url }))}
            square
            usedIn={ICON_USED_IN}
            note="Doubles as the host's default logo unless they upload their own override."
          />
          {IMAGE_FIELDS.map(f => (
            <ImageUploader
              key={f.key}
              category={category}
              fieldKey={f.key}
              label={f.label}
              value={draft[f.key]}
              onChange={(url) => setDraft(d => ({ ...d, [f.key]: url }))}
              square
              usedIn={f.used_in}
            />
          ))}
        </div>

        {/* Linked-from rollup with REAL names (expandable) */}
        {linkedTotal > 0 && (
          <div className="border-t border-stone-100 pt-3 mb-3" data-testid={`mc-cat-linked-${category.category_id}`}>
            <button
              type="button"
              onClick={() => setLinkedOpen(o => !o)}
              className="flex items-center justify-between w-full text-left hover:bg-stone-50 px-2 -mx-2 py-1 rounded"
              data-testid={`mc-cat-linked-toggle-${category.category_id}`}
            >
              <span className="text-[10px] uppercase tracking-wider text-stone-500">Linked from · {linkedTotal} items</span>
              {linkedOpen ? <ChevronDown className="w-3.5 h-3.5 text-stone-400" /> : <ChevronRight className="w-3.5 h-3.5 text-stone-400" />}
            </button>
            <div className="flex flex-wrap gap-1.5 mt-1.5">
              {Object.entries(linkedFrom).filter(([_, v]) => v > 0).map(([k, v]) => (
                <Badge key={k} variant="outline" className="text-[10px]">
                  {COLLECTION_LABELS[k] || k}: {v}
                </Badge>
              ))}
            </div>
            {linkedOpen && (
              <div className="mt-3 space-y-2" data-testid={`mc-cat-linked-detail-${category.category_id}`}>
                {Object.keys(linkedFrom).filter(k => (linkedFrom[k] || 0) > 0).map((coll) => {
                  const items = details.filter(d => d.collection === coll);
                  if (items.length === 0) return null;
                  return (
                    <div key={coll} className="bg-stone-50 rounded-lg px-3 py-2">
                      <p className="text-[10px] uppercase tracking-wider text-stone-500 mb-1.5">{COLLECTION_LABELS[coll] || coll} ({linkedFrom[coll]})</p>
                      <div className="flex flex-wrap gap-1">
                        {items.map((item, idx) => (
                          <span key={item.id || idx} className="text-[11px] bg-white border border-stone-200 rounded-full px-2 py-0.5 text-stone-700" title={item.label}>
                            {item.label}
                          </span>
                        ))}
                        {linkedFrom[coll] > items.length && (
                          <span className="text-[10px] text-stone-400 px-1 py-0.5">+ {linkedFrom[coll] - items.length} more</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-2 border-t border-stone-100 pt-3">
          <Button size="sm" variant="outline" onClick={() => setDraft(init)} disabled={!dirty}>Reset</Button>
          <Button size="sm" onClick={() => onSave(category.category_id, draft)} disabled={!dirty} className="bg-blue-700 hover:bg-blue-800 text-white" data-testid={`mc-cat-save-${category.category_id}`}>
            Save & sync
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function ImageUploader({ category, fieldKey, label, value, onChange, square, usedIn = [], note }) {
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("folder", `categories/${category.category_id}`);
      const r = await fetch(`${API}/upload/cloudinary`, { method: "POST", headers: auth(), body: fd });
      if (!r.ok) throw new Error("upload failed");
      const data = await r.json();
      onChange(data.secure_url || data.url);
      toast.success(`${label} uploaded - click Save & sync to persist`);
    } catch (e) { toast.error("Upload failed"); }
    finally { setUploading(false); }
  };
  return (
    <div data-testid={`mc-cat-${category.category_id}-${fieldKey}`}>
      <Label className="text-[10px] text-stone-500 block mb-1">{label}</Label>
      {/* Iter 9.40 Slice C⁸ - every category image is square so the preview matches
          how it renders in the sidebar / dashboard / Kanban. */}
      <div className="aspect-square w-full max-w-[140px] mx-auto flex items-center justify-center border-2 border-dashed border-stone-200 bg-stone-50 overflow-hidden mb-1.5 rounded-md">
        {value ? (
          <img
            src={value}
            alt={label}
            className="w-full h-full object-cover"
            onError={(e) => { e.target.style.display = "none"; const fb = e.target.parentElement.querySelector("[data-fb]"); if (fb) fb.style.display = "flex"; }}
          />
        ) : null}
        <ImgIcon data-fb className={`w-6 h-6 text-stone-300 ${value ? "hidden" : ""}`} style={{ display: value ? "none" : "block" }} />
      </div>
      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid={`mc-cat-${category.category_id}-${fieldKey}-file`} />
      <Button variant="outline" size="sm" className="w-full hover:bg-stone-900 hover:text-white" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid={`mc-cat-${category.category_id}-${fieldKey}-upload`}>
        {uploading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Upload className="w-3 h-3 mr-1" />}
        {value ? "Replace" : "Upload"}
      </Button>
      {(usedIn.length > 0 || note) && (
        <div className="mt-1.5" data-testid={`mc-cat-${category.category_id}-${fieldKey}-used`}>
          <p className="text-[9px] uppercase tracking-wider text-stone-400">Where it shows</p>
          {usedIn.length > 0 && (
            <ul className="text-[10px] text-stone-600 space-y-0.5 list-disc pl-3 mt-0.5">
              {usedIn.map((u, i) => <li key={i}>{u}</li>)}
            </ul>
          )}
          {note && <p className="text-[10px] text-stone-500 italic mt-1">{note}</p>}
        </div>
      )}
    </div>
  );
}
